/**
 * Sets a cookie.
 * @param {string} name - Name for the cookie.
 * @param {string} value - Value for the cookie.
 * @param {number} days - Number of days until the cookie should expire.
 */
function setCookie(name, value, days) {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
}

/**
 * Gets the value of a cookie (if it exists).
 * @param {string} name - Name of the cookie.
 * @returns {?string}
 */
function getCookie(name) {
  const cookieString = `; ${document.cookie}`;
  const cookies = cookieString.split(`; ${name}=`);

  if (cookies.length === 2) {
    return cookies.pop().split(';').shift();
  }

  return null;
}

/* global Modal */

if (!customElements.get('age-verification-pop-up')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class AgeVerificationPopUp extends Modal {
      constructor() {
        super();
        this.cookie = `${this.id}-dismissed`;
        this.cancelButton = this.querySelector('.js-cancel-button');
        this.cancelMessage = this.querySelector('.js-cancel-message');
        this.proceedButton = this.querySelector('.js-close-modal');

        // --- Province selection (single-step, QC popup mode only) --------------------
        // The dropdown lives INSIDE the same view as the age buttons. When required,
        // the proceed button stays disabled until a province is chosen. In ip mode
        // (ON store) or on non-CA markets the dropdown never shows — pure age gate.
        const routing = window.PVRouting;
        this.isPopupMode = Boolean(routing && routing.mode === 'popup');
        this.marketCA = String((window.theme && window.theme.localizationCountry) || '').toUpperCase() === 'CA';
        this.provinceStep = this.querySelector('[data-pv-province-step]');
        this.provinceSelect = this.querySelector('.js-pv-province-select');
        this.changeMode = false;

        this.provinceKnown = Boolean(routing && routing.getProvinceCookie && routing.getProvinceCookie());
        this.requireProvince = this.isPopupMode && this.marketCA && Boolean(this.provinceStep && this.provinceSelect);

        if (this.requireProvince) {
          this.buildProvinceOptions();
          this.provinceStep.removeAttribute('hidden');
          this.syncProceedDisabled();
          this.provinceSelect.addEventListener('change', this.syncProceedDisabled.bind(this));
        }

        this.cancelButton.addEventListener('click', this.handleCancelClick.bind(this));

        if (Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });

          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        } else {
          this.autoOpen();
        }
      }

      /** Age is confirmed if PVRouting says so, or the legacy dismissal cookie is present. */
      isAgeConfirmed() {
        const routing = window.PVRouting;
        if (routing && routing.isAgeConfirmed && routing.isAgeConfirmed()) return true;
        return Boolean(getCookie(this.cookie));
      }

      /** Decide whether to open on load. */
      autoOpen() {
        if (!this.isAgeConfirmed()) {
          this.open();
          return;
        }
        // Migration: age already confirmed (e.g. legacy cookie) but no province stored
        // yet on the CA market → still need the choice once.
        if (this.requireProvince && !this.provinceKnown) {
          this.open();
        }
      }

      /** Build the province <select> options from the single per-store routing config. */
      buildProvinceOptions() {
        const cfg = (window.theme && window.theme.provinceRouting) || {};
        const codes = Array.isArray(cfg.provinces) ? cfg.provinces : Object.keys(cfg.provinceToStore || {});
        const labels = (window.theme && window.theme.provinceLabels) || cfg.provinceLabels || {};
        if (!codes.length || !this.provinceSelect) {
          this.requireProvince = false;
          return;
        }
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.provinceSelect.dataset.placeholder || '—';
        placeholder.disabled = true;
        placeholder.selected = true;
        this.provinceSelect.appendChild(placeholder);
        codes.forEach((code) => {
          const opt = document.createElement('option');
          opt.value = String(code).toUpperCase();
          opt.textContent = labels[code] || labels[String(code).toUpperCase()] || code;
          this.provinceSelect.appendChild(opt);
        });
        // Preselect the stored choice (used by the header "change province" flow).
        const routing = window.PVRouting;
        const current = routing && routing.getProvinceCookie && routing.getProvinceCookie();
        if (current) this.provinceSelect.value = current;
      }

      /** Block submission until a province is chosen (when the dropdown is required). */
      syncProceedDisabled() {
        if (!this.proceedButton) return;
        const blocked = this.requireProvince && !(this.provinceSelect && this.provinceSelect.value);
        this.proceedButton.disabled = blocked;
        this.proceedButton.setAttribute('aria-disabled', String(blocked));
      }

      /**
       * Opens the modal.
       * @param {Element} opener - Modal opener element.
       */
      open(opener) {
        super.open(opener);
        this.removeEventListener('keyup', this.keyupHandler);
      }

      /**
       * Handle the cancel button click: redirect away when a link is configured
       * (section setting), otherwise reveal the cancel message.
       */
      handleCancelClick() {
        const url = this.cancelButton && this.cancelButton.dataset.cancelUrl;
        if (url) {
          window.location.href = url;
          return;
        }
        this.cancelMessage.removeAttribute('hidden');
      }

      /** Confirm age: write pv_age_ok (shared cookie) + the legacy cookie for back-compat. */
      confirmAge() {
        const routing = window.PVRouting;
        if (routing && routing.setAgeConfirmed) routing.setAgeConfirmed();
        setCookie(this.cookie, true, 3650);
      }

      /**
       * Handles 'click' events on the modal.
       * @param {object} evt - Event object.
       */
      handleClick(evt) {
        if (!evt.target.matches('.js-close-modal')) return;

        const routing = window.PVRouting;
        if (this.requireProvince) {
          const code = this.provinceSelect ? this.provinceSelect.value : '';
          if (!code || (routing && routing.isValidProvince && !routing.isValidProvince(code))) {
            evt.preventDefault();
            this.syncProceedDisabled();
            if (this.provinceSelect) this.provinceSelect.focus();
            return;
          }
          this.confirmAge();
          if (routing && routing.setProvince) {
            // Header-initiated change warns about cart loss; first visit does not.
            const result = routing.setProvince(code, {
              confirmCart: this.changeMode,
              explicit: true,
              source: this.changeMode ? 'change-province' : 'popup'
            });
            if (result && result.action === 'redirect') return; // page is navigating away
          }
          super.handleClick(evt);
          return;
        }

        // Pure age gate (ip mode / non-CA market) — original behaviour.
        super.handleClick(evt);
        this.confirmAge();
      }
    }

    customElements.define('age-verification-pop-up', AgeVerificationPopUp);
  });
}
